import type { ReportsKpiTone } from '@dataTypes/reports.type'

export type ReportKpiFooterMode =
  | 'percentChange'
  | 'pointChange'
  | 'percentOfPriorBase'
  | 'descriptor'

const formatSignedNumber = (value: number, digits: number): string => {
  const rounded = Number(value.toFixed(digits))
  if (rounded === 0) return digits === 0 ? '0' : (0).toFixed(digits)
  const abs = Math.abs(rounded).toFixed(digits)
  return rounded > 0 ? `+${abs}` : `-${abs}`
}

export const percentChangeVsPrior = (
  current: number,
  prior: number,
): { text: string; tone: ReportsKpiTone } => {
  if (!Number.isFinite(current) || !Number.isFinite(prior) || prior === 0) {
    return { text: '—', tone: 'neutral' }
  }
  const pct = ((current - prior) / Math.abs(prior)) * 100
  const text = `${formatSignedNumber(pct, 1)}% vs prior period`
  return { text, tone: pct > 0 ? 'positive' : 'neutral' }
}

export const pointChangeVsPrior = (
  current: number,
  prior: number,
): { text: string; tone: ReportsKpiTone } => {
  if (!Number.isFinite(current) || !Number.isFinite(prior)) {
    return { text: '—', tone: 'neutral' }
  }
  const pts = current - prior
  const text = `${formatSignedNumber(pts, 1)} pt vs prior period`
  return { text, tone: pts > 0 ? 'positive' : 'neutral' }
}

/** e.g. net active growth as share of prior active base. */
export const percentOfPriorBase = (
  delta: number,
  priorBase: number,
): { text: string; tone: ReportsKpiTone } => {
  if (
    !Number.isFinite(delta) ||
    !Number.isFinite(priorBase) ||
    priorBase === 0
  ) {
    return { text: '—', tone: 'neutral' }
  }
  const pct = (delta / Math.abs(priorBase)) * 100
  const text = `${formatSignedNumber(pct, 1)}% of prior active`
  return { text, tone: pct > 0 ? 'positive' : 'neutral' }
}

export const formatReportKpiFooter = (
  mode: ReportKpiFooterMode,
  current: number | undefined,
  prior: number | undefined,
  descriptor?: string,
): { text: string; tone: ReportsKpiTone } => {
  switch (mode) {
    case 'descriptor':
      return { text: descriptor ?? '—', tone: 'neutral' }
    case 'pointChange':
      return pointChangeVsPrior(current ?? NaN, prior ?? NaN)
    case 'percentOfPriorBase':
      return percentOfPriorBase(current ?? NaN, prior ?? NaN)
    case 'percentChange':
    default:
      return percentChangeVsPrior(current ?? NaN, prior ?? NaN)
  }
}
